var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { HttpClient, HttpParams, HttpResponse, } from '@angular/common/http';
import { ErrorHandler } from '@angular/core';
import { Auth } from '@freelancer/auth';
import * as Rx from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
function isDefined(x) {
    return x !== undefined;
}
/*
 * This removes the special parameter encoding done by Angular for apparently
 * no reason, see https://github.com/angular/angular/issues/18261.
 * FIXME: remove that when Angular has fixed it.
 */
var CustomEncoder = /** @class */ (function () {
    function CustomEncoder() {
    }
    CustomEncoder.prototype.encodeKey = function (key) {
        return encodeURIComponent(key);
    };
    CustomEncoder.prototype.encodeValue = function (value) {
        return encodeURIComponent(value);
    };
    CustomEncoder.prototype.decodeKey = function (key) {
        return decodeURIComponent(key);
    };
    CustomEncoder.prototype.decodeValue = function (value) {
        return decodeURIComponent(value);
    };
    return CustomEncoder;
}());
var FreelancerHttp = /** @class */ (function () {
    function FreelancerHttp(http, auth, errorHandler, freelancerHttpConfig) {
        this.http = http;
        this.auth = auth;
        this.errorHandler = errorHandler;
        this.freelancerHttpConfig = freelancerHttpConfig;
        // Add compact=true to strip out null values on the response.
        // Add new_errors=true for new error format
        this.requestParams = {
            compact: 'true',
            new_errors: 'true',
        };
    }
    /**
     * Utility function to merge the Auth headers with the request headers.
     * @param authHeaders HttpHeaders from Auth
     * @param requestHeaders HttpHeaders specified on the request options.
     */
    FreelancerHttp.prototype.mergeAuthHeaderWithRequestHeaders = function (authHeaders, requestHeaders) {
        if (!requestHeaders) {
            return authHeaders;
        }
        return authHeaders.keys().reduce(function (obj, key) {
            var values = authHeaders.getAll(key);
            return values ? requestHeaders.append(key, values) : requestHeaders;
        }, requestHeaders);
    };
    /**
     * Get the base URL to use, whether or not it's a GAF endpoint or a Freelancer
     * API endpoint.
     * @param config Configuration object.
     * @param config.isGaf True if GAF endpoint, false if API endpoint.
     */
    FreelancerHttp.prototype.getBaseUrl = function (config) {
        if (config === void 0) { config = {}; }
        return config.isGaf
            ? this.freelancerHttpConfig.ajaxBaseUrl
            : this.freelancerHttpConfig.apiBaseUrl;
    };
    /**
     * Format response body to the interface that we want to return.
     * For now it's only adding a status code into the response body.
     * @param response The HttpResponse object.
     */
    FreelancerHttp.prototype.formatResponseBody = function (response) {
        if (response.body === null) {
            console.error('No response body', response);
            return {
                status: 'error',
                errorCode: 'UNKNOWN_ERROR',
            };
        }
        if (response.body.status !== 'success') {
            console.error('Malformed backend response', response.body);
            return {
                status: 'error',
                errorCode: 'UNKNOWN_ERROR',
            };
        }
        return {
            status: response.body.status,
            result: response.body.result,
            requestId: response.body.request_id,
        };
    };
    FreelancerHttp.prototype.reportErrorResponse = function (error) {
        if (error.error instanceof Error) {
            // A client-side error occurred.
            this.errorHandler.handleError(error.error);
        }
        else {
            // We don't log backend errors in Sentry as there are already logged in
            // Kibana
            console.error(error.message, error.error);
        }
    };
    FreelancerHttp.prototype.formatErrorBody = function (error) {
        if (error.error && error.error.error !== undefined) {
            return {
                status: 'error',
                errorCode: error.error.error.code,
                requestId: error.error.request_id,
            };
        }
        // Another type of backend error occured, e.g. External LB or Varnish.
        return {
            status: 'error',
            errorCode: 'UNKNOWN_ERROR',
        };
    };
    /**
     * Helper function to convert an object to HttpParams, which is conformant
     * to the `application/x-www-form-urlencoded` MIME type.
     *
     * `undefined` values and empty arrays are not serialized.
     *  Non-empty array values have `[]` appended to their keys.
     *
     * @param paramsObject The object to convert. Limited to string, number,
     * boolean primitives as values, array containing those primitives, or
     * nested objects. This means NO nested arrays, e.g. [1, [2, 3]]
     */
    FreelancerHttp.prototype._serialize = function (paramsObject) {
        if (paramsObject === void 0) { paramsObject = {}; }
        if (paramsObject instanceof HttpParams) {
            return paramsObject;
        }
        return Object.entries(paramsObject).reduce(function (httpParams, _a) {
            var key = _a[0], value = _a[1];
            if (Array.isArray(value)) {
                return value
                    .filter(isDefined)
                    .reduce(function (innerParams, arrayItem) {
                    // FIXME: This is not allowed according to the type definition
                    // of RequestParamsObject, so this branch is unlikely to be taken
                    if (Array.isArray(arrayItem)) {
                        // Make sure that batching is handled here. Break all & into more
                        // params additions.
                        return String(arrayItem)
                            .split('&')
                            .reduce(function (innerInnerParams, arrayBatchItem) {
                            return innerInnerParams.append(key + "[]", typeof arrayBatchItem !== 'string'
                                ? JSON.stringify(arrayBatchItem)
                                : arrayBatchItem);
                        }, innerParams);
                    }
                    // Stringify the array item because we need a string and if it's an
                    // inner object just stringify to JSON.
                    return innerParams.append(key + "[]", typeof arrayItem !== 'string'
                        ? JSON.stringify(arrayItem)
                        : arrayItem);
                }, httpParams);
            }
            // Our REST API backend expects arrays foo=[1,2] to be encoded as
            // foo[]=1&foo[]=2 (but only for form-encoded payloads, see T74444)
            if (value !== undefined) {
                // Don't stringify the value if it's a string, otherwise you'll end up
                // with quotation marks surrounding the string (`"foo"` instead of `foo`).
                return httpParams.append(key, typeof value !== 'string' ? JSON.stringify(value) : value);
            }
            return httpParams;
        }, new HttpParams({ encoder: new CustomEncoder() }));
    };
    FreelancerHttp.prototype.get = function (endpoint, options) {
        var _this = this;
        if (options === void 0) { options = {}; }
        var baseUrl = this.getBaseUrl({ isGaf: options.isGaf });
        // Add webapp=1 for tracking in the backend to API requests
        var requestParams = options.isGaf
            ? __assign({}, options.params, this.requestParams) : __assign({}, options.params, { webapp: 1 }, this.requestParams);
        if (options.reportProgress) {
            return Rx.of(endpoint).pipe(withLatestFrom(this.auth.getAuthorizationHeader()), switchMap(function (_a) {
                var requestEndpoint = _a[0], authHeader = _a[1];
                return _this.http.get(baseUrl + "/" + requestEndpoint, {
                    reportProgress: options.reportProgress,
                    observe: 'events',
                    params: _this._serialize(requestParams),
                    headers: _this.mergeAuthHeaderWithRequestHeaders(authHeader, options.headers),
                });
            }), map(function (event) {
                if (event instanceof HttpResponse) {
                    return event.clone({
                        body: _this.formatResponseBody(event),
                    });
                }
                return event;
            }), catchError(function (error) {
                _this.reportErrorResponse(error);
                return Rx.of(new HttpResponse({
                    body: _this.formatErrorBody(error),
                }));
            }));
        }
        return Rx.of(endpoint).pipe(withLatestFrom(this.auth.getAuthorizationHeader()), switchMap(function (_a) {
            var requestEndpoint = _a[0], authHeader = _a[1];
            return _this.http.get(baseUrl + "/" + requestEndpoint, {
                reportProgress: options.reportProgress,
                observe: 'response',
                params: _this._serialize(requestParams),
                headers: _this.mergeAuthHeaderWithRequestHeaders(authHeader, options.headers),
            });
        }), map(this.formatResponseBody), catchError(function (error) {
            _this.reportErrorResponse(error);
            return Rx.of(_this.formatErrorBody(error));
        }));
    };
    FreelancerHttp.prototype.post = function (endpoint, body, options) {
        var _this = this;
        if (options === void 0) { options = {}; }
        var baseUrl = this.getBaseUrl({ isGaf: options.isGaf });
        var requestBody = options.serializeBody ? this._serialize(body) : body;
        if (options.reportProgress) {
            return Rx.of(endpoint).pipe(withLatestFrom(this.auth.getAuthorizationHeader()), switchMap(function (_a) {
                var requestEndpoint = _a[0], authHeader = _a[1];
                return _this.http.post(baseUrl + "/" + requestEndpoint, requestBody, {
                    reportProgress: options.reportProgress,
                    observe: 'events',
                    headers: _this.mergeAuthHeaderWithRequestHeaders(authHeader, options.headers),
                    params: _this.requestParams,
                });
            }), map(function (event) {
                if (event instanceof HttpResponse) {
                    return event.clone({
                        body: _this.formatResponseBody(event),
                    });
                }
                return event;
            }), catchError(function (error) {
                _this.reportErrorResponse(error);
                return Rx.of(new HttpResponse({
                    body: _this.formatErrorBody(error),
                }));
            }));
        }
        return Rx.of(endpoint).pipe(withLatestFrom(this.auth.getAuthorizationHeader()), switchMap(function (_a) {
            var requestEndpoint = _a[0], authHeader = _a[1];
            return _this.http.post(baseUrl + "/" + requestEndpoint, requestBody, {
                reportProgress: options.reportProgress,
                observe: 'response',
                headers: _this.mergeAuthHeaderWithRequestHeaders(authHeader, options.headers),
                params: _this.requestParams,
            });
        }), map(this.formatResponseBody), catchError(function (error) {
            _this.reportErrorResponse(error);
            return Rx.of(_this.formatErrorBody(error));
        }));
    };
    FreelancerHttp.prototype.put = function (endpoint, body, options) {
        var _this = this;
        if (options === void 0) { options = {}; }
        var baseUrl = this.getBaseUrl({ isGaf: options.isGaf });
        var requestBody = options.serializeBody ? this._serialize(body) : body;
        if (options.reportProgress) {
            return Rx.of(endpoint).pipe(withLatestFrom(this.auth.getAuthorizationHeader()), switchMap(function (_a) {
                var requestEndpoint = _a[0], authHeader = _a[1];
                return _this.http.put(baseUrl + "/" + requestEndpoint, requestBody, 
                // FIXME: <any> is needed because of
                // https://github.com/angular/angular/issues/23600
                {
                    reportProgress: options.reportProgress,
                    observe: 'events',
                    headers: _this.mergeAuthHeaderWithRequestHeaders(authHeader, options.headers),
                    params: _this.requestParams,
                });
            }), map(function (event) {
                if (event instanceof HttpResponse) {
                    return event.clone({
                        body: _this.formatResponseBody(event),
                    });
                }
                return event;
            }), catchError(function (error) {
                _this.reportErrorResponse(error);
                return Rx.of(new HttpResponse({
                    body: _this.formatErrorBody(error),
                }));
            }));
        }
        return Rx.of(endpoint).pipe(withLatestFrom(this.auth.getAuthorizationHeader()), switchMap(function (_a) {
            var requestEndpoint = _a[0], authHeader = _a[1];
            return _this.http.put(baseUrl + "/" + requestEndpoint, requestBody, {
                reportProgress: options.reportProgress,
                observe: 'response',
                headers: _this.mergeAuthHeaderWithRequestHeaders(authHeader, options.headers),
                params: _this.requestParams,
            });
        }), map(this.formatResponseBody), catchError(function (error) {
            _this.reportErrorResponse(error);
            return Rx.of(_this.formatErrorBody(error));
        }));
    };
    FreelancerHttp.prototype.delete = function (endpoint, options) {
        var _this = this;
        if (options === void 0) { options = {}; }
        var baseUrl = this.getBaseUrl({ isGaf: options.isGaf });
        if (options.reportProgress) {
            return Rx.of(endpoint).pipe(withLatestFrom(this.auth.getAuthorizationHeader()), switchMap(function (_a) {
                var requestEndpoint = _a[0], authHeader = _a[1];
                return _this.http.delete(baseUrl + "/" + requestEndpoint, {
                    reportProgress: options.reportProgress,
                    observe: 'events',
                    headers: _this.mergeAuthHeaderWithRequestHeaders(authHeader, options.headers),
                    params: _this.requestParams,
                });
            }), map(function (event) {
                if (event instanceof HttpResponse) {
                    return event.clone({
                        body: _this.formatResponseBody(event),
                    });
                }
                return event;
            }), catchError(function (error) {
                _this.reportErrorResponse(error);
                return Rx.of(new HttpResponse({
                    body: _this.formatErrorBody(error),
                }));
            }));
        }
        return Rx.of(endpoint).pipe(withLatestFrom(this.auth.getAuthorizationHeader()), switchMap(function (_a) {
            var requestEndpoint = _a[0], authHeader = _a[1];
            return _this.http.delete(baseUrl + "/" + requestEndpoint, {
                reportProgress: options.reportProgress,
                observe: 'response',
                headers: _this.mergeAuthHeaderWithRequestHeaders(authHeader, options.headers),
                params: _this.requestParams,
            });
        }), map(this.formatResponseBody), catchError(function (error) {
            _this.reportErrorResponse(error);
            return Rx.of(_this.formatErrorBody(error));
        }));
    };
    return FreelancerHttp;
}());
export { FreelancerHttp };
